import { Button, Divider, Form, notification, Row, Spin } from "antd";
import {
  DistributionPointRequestResource,
  DistributionPointResponseResource,
  getDistributionPointById,
  updateDistributionPoint,
  insertCompany,
  createDistributionPoint,
  RequestCompany,
  onboardingStatus,
} from "../../api/generated";
import { useClient } from "../../hooks/useClient";
import { useEffect, useState } from "react";
import { validate as isValidUUID } from "uuid";
import { withRouter, RouteComponentProps } from "react-router-dom";
import GeneralDataForm from "./components/form/GeneralDataForm";
import { useForm } from "antd/es/form/Form";
import AddressDataForm from "./components/form/AddressDataForm";
import ProductsDataForm from "./components/form/ProductsDataForm";
import MaxDifferenceDataForm from "./components/form/MaxDifferenceDataForm";
import { useCatalogsDistributionPoint } from "hooks/distributionPoints/useCatalogsDistributionPoint";
import { CV_CONTEXT_PATH, ToFirstWarehouseRegistry } from "security";

interface RouteParams {
  distributionPointId: string;
}

function mapToFormData(
  dpp?: DistributionPointResponseResource
): DistributionPointRequestResource {
  const [installationKey = undefined, installationKeyNumber = undefined] =
    dpp?.identificationKey?.split("-") ?? [];
  const mapped: DistributionPointRequestResource = {
    rfc: dpp?.rfc,
    companyActivityType: dpp?.companyActivityType,
    legalRepresentative: dpp?.legalRepresentative,
    legalRepresentativeRfc: dpp?.legalRepresentativeRfc,
    street: dpp?.street,
    number: dpp?.number,
    insideNumber: dpp?.insideNumber,
    colony: dpp?.colony,
    municipality: dpp?.municipality,
    state: dpp?.state,
    postalCode: dpp?.postalCode,
    latitude: dpp?.latitude,
    longitude: dpp?.longitude,
    senerLicense: dpp?.senerLicense,
    operationLicense: dpp?.operationLicense,
    installationKey,
    installationKeyNumber,
    character: dpp?.character,
    regulatoryCharacterField1: dpp?.regulatoryCharacterField1,
    description: dpp?.description,
    emails: dpp?.emails,
    maxDifferencePorcentage: dpp?.maxDifferencePorcentage,
    name: dpp?.name,
    timeZone: dpp?.localTimeZone,
    permissionModality: dpp?.regulatoryCharacterField2,
    productsSold: dpp?.products
      ?.map((p) => p.idProduct)
      .filter((p) => p !== undefined),
    subsidiary: dpp?.subsidiary,
  };
  return mapped;
}

function UpsertDistributionPoint({ match }: RouteComponentProps<RouteParams>) {
  const { distributionPointId } = match.params;
  const { client } = useClient();
  const {
    activityTypes,
    installationKeys,
    regulatoryPermissionKeys,
    products,
    availableEmails,
    isLoading: isLoadingCatalogs,
  } = useCatalogsDistributionPoint();

  const [isLoading, setIsLoading] = useState(true);
  const [formData] = useForm<DistributionPointRequestResource>();
  const [isConfiguredApp, setIsConfiguredApp] = useState(true);
  const [isOnOnboarding, setIsOnOnboarding] = useState(true);

  useEffect(() => {
    if (isValidUUID(distributionPointId)) {
      getDistributionPointById({ path: { id: distributionPointId }, client })
        .then(({ data }) => {
          formData.setFieldsValue(mapToFormData(data));
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      onboardingStatus({ client })
        .then(({ data }) => {
          if (data) {
            const onboarding: boolean =
              !data.companyRegistered ||
              !data.firstWarehouseRegistered ||
              !data.firstContainerRegistered;
            setIsOnOnboarding(onboarding);
            if (onboarding) {
              setIsConfiguredApp(false);
              if (data.companyRegistered) {
                window.location.replace(
                  `${CV_CONTEXT_PATH}/distributionPoints/${data.companyRegistered}`
                );
              }
            }
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [client, distributionPointId]);

  function callApi<T>(promise: Promise<T>, onSuccess?: () => void) {
    setIsLoading(true);
    const storeAction: string = isValidUUID(distributionPointId)
      ? "actualizar"
      : "registrar";

    promise
      .then(() => {
        notification.success({
          message: `Exito al ${storeAction} el punto de distribucion`,
        });
        onSuccess?.();
      })
      .catch((err) => {
        notification.error({
          message: `Error al ${storeAction} el punto de distribucion`,
          description: `${err}`,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function handleSubmit() {
    formData
      .validateFields()
      .then(() => {
        const values = formData.getFieldsValue();
        setIsLoading(true);
        if (isValidUUID(distributionPointId)) {
          // PUT: Actualiza un Punto de distribución
          callApi(
            updateDistributionPoint({
              path: { id: distributionPointId },
              body: values,
            }),
            () => {
              if (isOnOnboarding) {
                ToFirstWarehouseRegistry();
              } else {
                history.back();
              }
            }
          );
        } else {
          // POST: Registra la compañia
          if (!isConfiguredApp) {
            const companyData: RequestCompany = {
              ...values,
              permissionNumber: values.permissionModality,
              contractAsignationNumber: values.regulatoryCharacterField1,
              naturalGasStorageInstalation: values.regulatoryCharacterField1,
            };

            callApi(insertCompany({ client, body: companyData }), () => {
              ToFirstWarehouseRegistry();
            });
          } else {
            // POST: Registra un nuevo punto de distribución
            callApi(createDistributionPoint({ client, body: values }), () => {
              history.back();
            });
          }
        }
      })
      .catch(() => {
        notification.error({
          message: "Error en validaciones",
          description: "Se han detectado valores inválidos en el formulario",
        });
      });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
      <Spin
        size="large"
        tip="Cargando..."
        spinning={isLoading || isLoadingCatalogs}
      >
        <Form layout="vertical" form={formData} scrollToFirstError>
          <Row>
            <span className="instructions">
              Llena los campos a continuación para registrar el punto de
              distribución
            </span>
            <Form.Item extra="* Campos obligatorios" />
          </Row>
          <GeneralDataForm
            formData={formData}
            catalogActivityTypes={activityTypes}
            installationKeys={installationKeys}
            regulatoryPermissionKeys={regulatoryPermissionKeys}
          />
          <AddressDataForm />
          <ProductsDataForm products={products} />
          <MaxDifferenceDataForm
            formData={formData}
            globalEmails={availableEmails}
          />

          <Divider />
          <Form.Item style={{ float: "right" }}>
            {isConfiguredApp && (
              <Button
                className="customized-default"
                style={{ marginLeft: 16 }}
                onClick={() => {
                  history.back();
                }}
              >
                Regresar
              </Button>
            )}
            <Button
              className="customized-primary"
              style={{ marginLeft: 16, width: 170, color: "white" }}
              htmlType="submit"
              onClick={() => {
                handleSubmit();
              }}
            >
              {isValidUUID(distributionPointId) ? "Actualizar" : "Registrar"}
            </Button>
          </Form.Item>
          <Divider />
        </Form>
      </Spin>
    </div>
  );
}

export default withRouter(UpsertDistributionPoint);
